/**
 * 数据迁移工具
 * 
 * 确保100%向后兼容现有.牛马文件格式
 * 支持从旧版本数据结构迁移到新版本
 */

import { calculateOvertimeHours, calculateRestHours } from '../core/TimeCalculator.js';
import { updateOvertimeRecordsRemainingTime } from '../core/ConsumptionAllocator.js';

/**
 * 数据版本定义
 */
export const DATA_VERSIONS = {
  V1_0: '1.0.0',  // 原始版本
  V2_0: '2.0.0'   // 重构版本
};

/**
 * 检测数据版本
 * 
 * @param {Object} data - 数据对象
 * @returns {string} 数据版本
 */
export function detectDataVersion(data) {
  if (!data || typeof data !== 'object') {
    return DATA_VERSIONS.V1_0;
  }
  
  // 检查是否有版本标识
  if (data.version) {
    return data.version;
  }
  
  // 检查数据结构特征来判断版本
  if (data.overtimeRecords && data.consumptionRecords && data.restRecords) {
    return DATA_VERSIONS.V1_0;
  }
  
  return DATA_VERSIONS.V1_0;
}

/**
 * 迁移数据到最新版本
 * 
 * @param {Object} data - 原始数据
 * @returns {Object} 迁移后的数据
 */
export function migrateToLatestVersion(data) {
  const version = detectDataVersion(data);
  
  switch (version) {
    case DATA_VERSIONS.V1_0:
      return migrateFromV1ToV2(data);
    case DATA_VERSIONS.V2_0:
      return data; // 已经是最新版本
    default:
      console.warn(`未知的数据版本: ${version}，尝试按V1.0处理`);
      return migrateFromV1ToV2(data);
  }
}

/**
 * 从V1.0迁移到V2.0
 * 
 * @param {Object} oldData - V1.0格式的数据
 * @returns {Object} V2.0格式的数据
 */
function migrateFromV1ToV2(oldData) {
  console.log('开始数据迁移：V1.0 -> V2.0');
  
  // 验证输入数据
  if (!oldData || typeof oldData !== 'object') {
    console.warn('无效的输入数据，使用默认数据结构');
    return createDefaultDataStructure();
  }
  
  const migratedData = {
    version: DATA_VERSIONS.V2_0,
    migratedAt: new Date().toISOString(),
    originalVersion: DATA_VERSIONS.V1_0
  };
  
  // 迁移加班记录
  migratedData.overtimeRecords = migrateOvertimeRecords(oldData.overtimeRecords || []);
  
  // 迁移消费记录（保持原有格式，这是核心业务逻辑）
  migratedData.consumptionRecords = migrateConsumptionRecords(oldData.consumptionRecords || []);
  
  // 迁移调休记录
  migratedData.restRecords = migrateRestRecords(oldData.restRecords || []);
  
  // 迁移年假信息
  migratedData.annualLeave = migrateAnnualLeave(oldData.annualLeave || {});
  
  // 迁移年假记录
  migratedData.annualLeaveRecords = oldData.annualLeaveRecords || [];
  
  // 清理0小时的消费记录（这些可能是旧版本产生的无效记录）
  migratedData.consumptionRecords = migratedData.consumptionRecords.filter(record => {
    if (record.deductionTime <= 0) {
      console.log('清理0小时消费记录:', record);
      return false;
    }
    return true;
  });

  // 重新计算加班记录的剩余时间（使用新的计算逻辑）
  migratedData.overtimeRecords = updateOvertimeRecordsRemainingTime(
    migratedData.overtimeRecords,
    migratedData.consumptionRecords
  );
  
  // 数据一致性检查
  const validationResult = validateMigratedData(migratedData);
  if (!validationResult.isValid) {
    console.warn('数据迁移后发现不一致性问题:', validationResult.errors);
  }
  
  console.log('数据迁移完成');
  return migratedData;
}

/**
 * 迁移加班记录
 */
function migrateOvertimeRecords(oldRecords) {
  return oldRecords.map(record => {
    // 使用新的计算逻辑重新计算时长
    const newDuration = calculateOvertimeHours(record.startTime, record.endTime);
    
    return {
      id: record.id,
      startTime: record.startTime,
      endTime: record.endTime,
      duration: newDuration,
      remainingTime: record.remainingTime || newDuration, // 临时值，后续会重新计算
      createdAt: record.id || new Date().toISOString()
    };
  });
}

/**
 * 迁移消费记录
 */
function migrateConsumptionRecords(oldRecords) {
  // 消费记录是核心业务逻辑，保持原有格式不变
  return oldRecords.map(record => ({
    id: record.id,
    overtimeId: record.overtimeId,
    restStartTime: record.restStartTime,
    restEndTime: record.restEndTime,
    consumptionStartTime: record.consumptionStartTime,
    consumptionEndTime: record.consumptionEndTime,
    deductionTime: record.deductionTime,
    type: record.type || '调休'
  }));
}

/**
 * 迁移调休记录
 */
function migrateRestRecords(oldRecords) {
  return oldRecords.map(record => {
    // 使用新的计算逻辑重新计算时长（按摆烂时间计算，扣除午休）
    const newDuration = calculateRestHours(record.txbegintime, record.txendtime);
    
    return {
      id: record.id,
      txbegintime: record.txbegintime,
      txendtime: record.txendtime,
      duration: newDuration,
      createdAt: record.id || new Date().toISOString()
    };
  });
}

/**
 * 迁移年假信息
 */
function migrateAnnualLeave(oldAnnualLeave) {
  // 如果已经有完整的新格式数据（包含有效的 totalHours），直接使用
  if (typeof oldAnnualLeave.totalHours === 'number' && oldAnnualLeave.totalHours >= 0) {
    const totalHours = oldAnnualLeave.totalHours;
    const totalDays = oldAnnualLeave.totalDays || Math.floor(totalHours / 8);
    const usedHours = oldAnnualLeave.usedHours || 0;
    const remainingHours = totalHours - usedHours;

    return {
      totalDays,
      totalHours,
      usedHours: Math.max(0, usedHours),
      remainingHours: Math.max(0, remainingHours)
    };
  }

  // 旧格式：只有 totalDays，按天数 * 8小时计算
  const totalDays = oldAnnualLeave.totalDays || 0;
  const totalHours = totalDays * 8;
  const usedHours = oldAnnualLeave.usedHours || 0;

  // 确保 remainingHours 不超过 totalHours
  let remainingHours = totalHours - usedHours;
  remainingHours = Math.max(0, Math.min(remainingHours, totalHours));

  return {
    totalDays,
    totalHours,
    usedHours: Math.max(0, usedHours),
    remainingHours
  };
}

/**
 * 创建默认数据结构
 */
function createDefaultDataStructure() {
  return {
    version: DATA_VERSIONS.V2_0,
    createdAt: new Date().toISOString(),
    overtimeRecords: [],
    consumptionRecords: [],
    restRecords: [],
    annualLeave: {
      totalDays: 0,
      totalHours: 0,
      usedHours: 0,
      remainingHours: 0
    },
    annualLeaveRecords: []
  };
}

/**
 * 验证迁移后的数据
 */
function validateMigratedData(data) {
  const errors = [];
  const warnings = [];
  
  // 检查必需字段
  if (!data.version) {
    errors.push('缺少版本信息');
  }
  
  if (!Array.isArray(data.overtimeRecords)) {
    errors.push('加班记录格式错误');
  }
  
  if (!Array.isArray(data.consumptionRecords)) {
    errors.push('消费记录格式错误');
  }
  
  if (!Array.isArray(data.restRecords)) {
    errors.push('调休记录格式错误');
  }
  
  // 检查数据一致性
  data.overtimeRecords.forEach(record => {
    if (!record.id || !record.startTime || !record.endTime) {
      errors.push(`加班记录 ${record.id || '未知'} 缺少必需字段`);
    }
    
    if (record.duration <= 0) {
      warnings.push(`加班记录 ${record.id} 的时长为0或负数`);
    }
    
    if (record.remainingTime < 0) {
      warnings.push(`加班记录 ${record.id} 的剩余时间为负数`);
    }
  });
  
  data.restRecords.forEach(record => {
    if (!record.id || !record.txbegintime || !record.txendtime) {
      errors.push(`调休记录 ${record.id || '未知'} 缺少必需字段`);
    }
    
    if (record.duration <= 0) {
      warnings.push(`调休记录 ${record.id} 的时长为0或负数`);
    }
  });
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
}

/**
 * 导出数据（保持向后兼容）
 * 
 * @param {Object} data - 当前数据
 * @param {string} format - 导出格式 ('v1' | 'v2')
 * @returns {Object} 导出的数据
 */
export function exportData(data, format = 'v2') {
  if (format === 'v1') {
    // 导出为V1.0格式，确保旧版本程序可以读取
    return {
      overtimeRecords: data.overtimeRecords.map(record => ({
        id: record.id,
        startTime: record.startTime,
        endTime: record.endTime,
        duration: record.duration,
        remainingTime: record.remainingTime
      })),
      consumptionRecords: data.consumptionRecords,
      restRecords: data.restRecords.map(record => ({
        id: record.id,
        txbegintime: record.txbegintime,
        txendtime: record.txendtime,
        duration: record.duration
      })),
      annualLeave: {
        totalDays: data.annualLeave.totalDays,
        usedHours: data.annualLeave.usedHours
      },
      annualLeaveRecords: data.annualLeaveRecords
    };
  }
  
  // 默认导出V2.0格式
  return data;
}

/**
 * 数据清理工具
 * 
 * @param {Object} data - 数据对象
 * @returns {Object} 清理结果
 */
export function cleanupData(data) {
  let cleanedCount = 0;
  const cleanedData = { ...data };
  
  // 清理无效的加班记录
  const validOvertimeRecords = cleanedData.overtimeRecords.filter(record => {
    const isValid = record.id && record.startTime && record.endTime && record.duration > 0;
    if (!isValid) cleanedCount++;
    return isValid;
  });
  
  // 清理无效的调休记录
  const validRestRecords = cleanedData.restRecords.filter(record => {
    const isValid = record.id && record.txbegintime && record.txendtime && record.duration > 0;
    if (!isValid) cleanedCount++;
    return isValid;
  });
  
  // 清理孤立的消费记录（没有对应的加班记录或调休记录）
  const validOvertimeIds = new Set(validOvertimeRecords.map(r => r.id));
  const validRestTimeRanges = new Set(validRestRecords.map(r => `${r.txbegintime}-${r.txendtime}`));
  
  const validConsumptionRecords = cleanedData.consumptionRecords.filter(record => {
    const hasValidOvertime = !record.overtimeId || validOvertimeIds.has(record.overtimeId);
    const hasValidRest = validRestTimeRanges.has(`${record.restStartTime}-${record.restEndTime}`);
    const hasValidTime = record.deductionTime > 0; // 过滤掉0小时的记录
    const isValid = hasValidOvertime && hasValidRest && hasValidTime;
    if (!isValid) cleanedCount++;
    return isValid;
  });
  
  cleanedData.overtimeRecords = validOvertimeRecords;
  cleanedData.restRecords = validRestRecords;
  cleanedData.consumptionRecords = validConsumptionRecords;
  
  return {
    data: cleanedData,
    cleanedCount
  };
}
